import type { Identity } from '@clockworklabs/spacetimedb-sdk'
import _ from 'lodash'
import type { DbConnection, EventContext, RemoteTables } from '~/assets/bindings'

const useQuery = <T, K extends string | number = number>(
  connection: Ref<DbConnection | null>,
  table: keyof RemoteTables,
  query: Ref<string>,
  extractor: (item: T) => K
): Ref<Record<K, T>> => {
  if (!connection) return ref({}) as Ref<Record<K, T>>
  const rows = ref<Record<K, T>>(
    Object.fromEntries(connection.value?.db[table].tableCache.iter().map((p: T) => [extractor(p), p]) ?? []) as Record<
      K,
      T
    >
  )

  const onInsert = (_ctx: EventContext, row: T) => {
    rows.value[extractor(row)] = row
  }

  const onDelete = (_ctx: EventContext, row: T) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete rows.value[extractor(row)]
  }

  const onUpdate = (ctx: EventContext, oldRow: T, newRow: T) => {
    onDelete(ctx, oldRow)
    onInsert(ctx, newRow)
  }

  const subscribe = (sql: string) => {
    if (!sql) return
    console.log('subscribing to ', sql)
    connection.value?.subscriptionBuilder().subscribe(sql)
    connection.value?.db[table].onInsert(onInsert as any)
    connection.value?.db[table].onDelete(onDelete as any)
    connection.value?.db[table].onUpdate(onUpdate as any)
  }

  const unsubscribe = () => {
    connection.value?.db[table].removeOnInsert(onInsert as any)
    connection.value?.db[table].removeOnDelete(onDelete as any)
    connection.value?.db[table].removeOnUpdate(onUpdate as any)
  }

  onMounted(() => {
    subscribe(query.value)
  })

  onUnmounted(() => {
    unsubscribe()
  })

  watch(query, (sql) => {
    unsubscribe()
    subscribe(sql)
  })

  watch(connection, (db) => {
    if (db?.isActive) subscribe(query.value)
  })

  return rows as Ref<Record<K, T>>
}

export default useQuery

export const logDb = <T>(x: T): T => {
  console.log(
    JSON.stringify(
      x,
      (_k, v) => {
        if (v === undefined) return null
        if (v?.__identity__) return (v as Identity).toHexString().substring(0, 16)
        return v
      },
      2
    )
  )
  return x
}
